/**
 * `useStepUpGate(action)` — the SPA seam for invoking a step-up-OTP-gated
 * mutation.
 *
 * The hook owns:
 *   - The dialog's open state
 *   - The pending thunk (your gated mutation, captured at `execute()` time)
 *   - The "currently authorising" flag for UI spinners
 *
 * Call sites pass `gate.dialog` into their JSX (renders the OTP modal
 * when needed) and `gate.execute(thunk, prompt)` to trigger the gated
 * action:
 *
 * ```tsx
 * const gate = useStepUpGate(STEP_UP_ACTIONS.BILLS_BATCH_APPROVE);
 *
 * const approveBills = () => gate.execute(
 *   () => trpc.bills.batchApprove.mutate({ billIds }),
 *   { title: `Approve ${billIds.length} bills`, description: '...' },
 * );
 *
 * return (
 *   <>
 *     <button onClick={approveBills}>Approve selected</button>
 *     {gate.dialog}
 *   </>
 * );
 * ```
 *
 * The hook also tries the thunk OPTIMISTICALLY first — if the user
 * already has fresh step-up evidence for this action (e.g. they just
 * verified for a previous batch < 5 min ago), the call succeeds
 * without opening the dialog at all. Only on `UNAUTHORIZED ·
 * step-up-required` / `step-up-expired` does the dialog open and the
 * thunk re-run after verification.
 *
 * Why optimistic-first instead of always-prompt: the 5-minute window is
 * a real UX seam — operators batch-approving back-to-back should not
 * have to re-OTP per click. The server is the source of truth; this
 * hook just avoids unnecessary prompts when the server would have
 * accepted the call anyway.
 *
 * @see step-up-dialog.tsx — the modal mounted by `gate.dialog`
 * @see packages/api-contract/src/step-up-actions.ts — action registry
 */
import { useCallback, useRef, useState } from "react";
import type { StepUpAction } from "@flint/api-contract";
import { StepUpDialog } from "./step-up-dialog.tsx";

/**
 * Operator-facing copy for the dialog header. Passed into `execute()`
 * so each call site can describe what's being authorised.
 */
export interface StepUpPrompt {
  title: string;
  description: string;
}

export interface StepUpGate {
  /**
   * Run the gated thunk. If the session already has fresh evidence the
   * thunk runs immediately; otherwise the dialog opens, the operator
   * verifies, then the thunk runs. Returns a promise that resolves with
   * the thunk's value on success, or `undefined` if the operator
   * cancelled the dialog.
   *
   * Errors from the thunk that aren't step-up-related propagate up so
   * the caller can show its own toast / handle them.
   */
  execute: <T>(thunk: () => Promise<T>, prompt: StepUpPrompt) => Promise<T | undefined>;
  /** Place `{gate.dialog}` somewhere in your component tree. */
  dialog: React.ReactElement;
  /** True while the dialog is open OR a thunk is mid-flight. */
  busy: boolean;
}

export function useStepUpGate(action: StepUpAction): StepUpGate {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState<StepUpPrompt>({ title: "", description: "" });
  // Captured thunk + resolver. Stored in a ref so the dialog's
  // `onVerified` callback can read them without stale-closure issues.
  const pendingRef = useRef<{
    thunk: () => Promise<unknown>;
    resolve: (value: unknown) => void;
    reject: (err: unknown) => void;
  } | null>(null);

  const execute = useCallback(
    async <T,>(thunk: () => Promise<T>, p: StepUpPrompt): Promise<T | undefined> => {
      setBusy(true);
      try {
        // Optimistic first attempt — if step-up evidence is still
        // fresh for this action, the server lets it through.
        return await thunk();
      } catch (err) {
        if (!isStepUpRequired(err)) throw err;
        // Step-up needed — open the dialog and wait for the operator.
        return await new Promise<T | undefined>((resolve, reject) => {
          pendingRef.current = {
            thunk: thunk as () => Promise<unknown>,
            resolve: (value) => resolve(value as T),
            reject,
          };
          setPrompt(p);
          setOpen(true);
        });
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const handleVerified = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    setBusy(true);
    try {
      const value = await pending.thunk();
      pending.resolve(value);
    } catch (err) {
      pending.reject(err);
    } finally {
      setBusy(false);
      // Close the dialog AFTER the thunk runs. The dialog itself no
      // longer closes on verify success — see the comment in
      // step-up-dialog.tsx for the race that motivates this ordering.
      setOpen(false);
    }
  }, []);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      // Operator cancelled — resolve the outer promise with `undefined`
      // so the caller can distinguish cancel from success/error. Same
      // contract as a native confirm() returning false.
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.resolve(undefined);
    }
  }, []);

  const dialog = (
    <StepUpDialog
      open={open}
      onOpenChange={handleOpenChange}
      action={action}
      title={prompt.title}
      description={prompt.description}
      onVerified={() => void handleVerified()}
    />
  );

  return { execute, dialog, busy };
}

/**
 * Recognise the server's "step-up required / expired" errors so the
 * hook can open the dialog. Other errors propagate up to the caller.
 *
 * Detection has two paths:
 *   1. Structured `data.cause.reason` (preferred) — set by the server's
 *      errorFormatter when the TRPCError's cause is a plain object with
 *      a `reason` discriminator.
 *   2. Message fallback — tRPC's default serializer drops `cause` from
 *      the wire; the formatter opt-in fixes that, but the message check
 *      keeps the hook working even when the cause path drops.
 *
 * Both `step-up-required` and `step-up-expired` trigger the dialog —
 * we re-verify either way.
 */
function isStepUpRequired(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    data?: { code?: string; cause?: { reason?: string } };
    message?: string;
  };
  if (e.data?.code !== "UNAUTHORIZED") return false;
  const reason = e.data?.cause?.reason;
  if (reason === "step-up-required" || reason === "step-up-expired") return true;
  const msg = e.message ?? "";
  return /step[-\s]?up otp/i.test(msg);
}
